import { useState } from 'react';
import { Settings, Eye, EyeOff, Globe, Users, Lock, Bell, Shield, Smartphone } from 'lucide-react';

interface PrivacySettings {
  profileVisibility: 'public' | 'followers' | 'private';
  statsVisibility: 'public' | 'followers' | 'private';
  mediaVisibility: 'public' | 'followers' | 'private';
  contactInfo: 'public' | 'followers' | 'private';
  showOnlineStatus: boolean;
  allowMessages: 'everyone' | 'followers' | 'none';
  showInSearch: boolean;
}

interface NotificationSettings {
  newFollowers: boolean;
  messages: boolean;
  achievements: boolean;
  competitions: boolean;
  mentions: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
}

interface ProfileSettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

const ProfileSettings = ({ isOpen, onClose }: ProfileSettingsProps) => {
  const [activeTab, setActiveTab] = useState<'privacy' | 'notifications' | 'social'>('privacy');
  const [privacySettings, setPrivacySettings] = useState<PrivacySettings>({
    profileVisibility: 'public',
    statsVisibility: 'public',
    mediaVisibility: 'public',
    contactInfo: 'followers',
    showOnlineStatus: true,
    allowMessages: 'followers',
    showInSearch: true
  });
  const [notificationSettings, setNotificationSettings] = useState<NotificationSettings>({
    newFollowers: true,
    messages: true,
    achievements: true,
    competitions: true,
    mentions: true,
    emailNotifications: true,
    pushNotifications: true
  });

  if (!isOpen) return null;

  const tabs = [
    { id: 'privacy', label: 'Privacidad', icon: <Shield size={18} /> },
    { id: 'notifications', label: 'Notificaciones', icon: <Bell size={18} /> },
    { id: 'social', label: 'Redes Sociales', icon: <Globe size={18} /> }
  ];

  const visibilityOptions = [
    { value: 'public', label: 'Público', icon: <Globe size={16} />, description: 'Visible para todos' },
    { value: 'followers', label: 'Seguidores', icon: <Users size={16} />, description: 'Solo mis seguidores' },
    { value: 'private', label: 'Privado', icon: <Lock size={16} />, description: 'Solo yo' }
  ];

  const VisibilitySelector = ({ 
    value, 
    onChange, 
    label 
  }: { 
    value: string; 
    onChange: (value: string) => void; 
    label: string;
  }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      <div className="grid grid-cols-3 gap-2">
        {visibilityOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => onChange(option.value)}
            className={`p-3 rounded-lg border text-left transition-colors duration-200 ${
              value === option.value
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
          >
            <div className="flex items-center mb-1">
              {option.icon}
              <span className="ml-2 text-sm font-medium text-gray-900 dark:text-white">
                {option.label}
              </span>
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              {option.description}
            </p>
          </button>
        ))}
      </div>
    </div>
  );

  const ToggleSwitch = ({ 
    checked, 
    onChange, 
    label, 
    description 
  }: { 
    checked: boolean; 
    onChange: (checked: boolean) => void; 
    label: string;
    description?: string;
  }) => (
    <div className="flex items-center justify-between py-3">
      <div>
        <div className="text-sm font-medium text-gray-900 dark:text-white">{label}</div>
        {description && (
          <div className="text-xs text-gray-600 dark:text-gray-400">{description}</div>
        )}
      </div>
      <button
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
          checked ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Settings className="h-6 w-6 text-gray-900 dark:text-white mr-2" />
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Configuración del Perfil</h2>
            </div>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <div className="flex">
          {/* Sidebar */}
          <div className="w-64 border-r border-gray-200 dark:border-gray-700 p-4">
            <nav className="space-y-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`w-full flex items-center px-3 py-2 rounded-lg text-left transition-colors duration-200 ${
                    activeTab === tab.id
                      ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {tab.icon}
                  <span className="ml-3">{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            {activeTab === 'privacy' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Control de Privacidad
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Controla quién puede ver tu información y contenido
                  </p>
                </div>

                <VisibilitySelector
                  value={privacySettings.profileVisibility}
                  onChange={(value) => setPrivacySettings({
                    ...privacySettings,
                    profileVisibility: value as any
                  })}
                  label="Visibilidad del Perfil"
                />

                <VisibilitySelector
                  value={privacySettings.statsVisibility}
                  onChange={(value) => setPrivacySettings({
                    ...privacySettings,
                    statsVisibility: value as any
                  })}
                  label="Estadísticas Deportivas"
                />

                <VisibilitySelector
                  value={privacySettings.mediaVisibility}
                  onChange={(value) => setPrivacySettings({
                    ...privacySettings,
                    mediaVisibility: value as any
                  })}
                  label="Galería Multimedia"
                />

                <VisibilitySelector
                  value={privacySettings.contactInfo}
                  onChange={(value) => setPrivacySettings({
                    ...privacySettings,
                    contactInfo: value as any
                  })}
                  label="Información de Contacto"
                />

                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                    Configuraciones Adicionales
                  </h4>
                  
                  <ToggleSwitch
                    checked={privacySettings.showOnlineStatus}
                    onChange={(checked) => setPrivacySettings({
                      ...privacySettings,
                      showOnlineStatus: checked
                    })}
                    label="Mostrar estado en línea"
                    description="Permite que otros vean cuando estás activa"
                  />

                  <ToggleSwitch
                    checked={privacySettings.showInSearch}
                    onChange={(checked) => setPrivacySettings({
                      ...privacySettings,
                      showInSearch: checked
                    })}
                    label="Aparecer en búsquedas"
                    description="Permite que otros te encuentren buscando tu nombre"
                  />

                  <div className="py-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Permitir mensajes de
                    </label>
                    <select
                      value={privacySettings.allowMessages}
                      onChange={(e) => setPrivacySettings({
                        ...privacySettings,
                        allowMessages: e.target.value as any
                      })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="everyone">Cualquier persona</option>
                      <option value="followers">Solo seguidores</option>
                      <option value="none">Nadie</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Notificaciones
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Elige qué notificaciones quieres recibir
                  </p>
                </div>

                <div className="space-y-1">
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                    Actividad en la Plataforma
                  </h4>
                  
                  <ToggleSwitch
                    checked={notificationSettings.newFollowers}
                    onChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      newFollowers: checked
                    })}
                    label="Nuevos seguidores"
                    description="Cuando alguien comience a seguirte"
                  />

                  <ToggleSwitch
                    checked={notificationSettings.messages}
                    onChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      messages: checked
                    })}
                    label="Mensajes"
                    description="Cuando recibas un mensaje directo"
                  />

                  <ToggleSwitch
                    checked={notificationSettings.mentions}
                    onChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      mentions: checked
                    })}
                    label="Menciones"
                    description="Cuando alguien te mencione en una publicación"
                  />
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                    Deportes y Competencias
                  </h4>
                  
                  <ToggleSwitch
                    checked={notificationSettings.achievements}
                    onChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      achievements: checked
                    })}
                    label="Logros y récords"
                    description="Actualizaciones sobre tus logros deportivos"
                  />

                  <ToggleSwitch
                    checked={notificationSettings.competitions}
                    onChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      competitions: checked
                    })}
                    label="Competencias"
                    description="Recordatorios y actualizaciones de competencias"
                  />
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-3">
                    Métodos de Entrega
                  </h4>
                  
                  <ToggleSwitch
                    checked={notificationSettings.pushNotifications}
                    onChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      pushNotifications: checked
                    })}
                    label="Notificaciones push"
                    description="Recibir notificaciones en tu dispositivo"
                  />

                  <ToggleSwitch
                    checked={notificationSettings.emailNotifications}
                    onChange={(checked) => setNotificationSettings({
                      ...notificationSettings,
                      emailNotifications: checked
                    })}
                    label="Notificaciones por email"
                    description="Recibir resúmenes por correo electrónico"
                  />
                </div>
              </div>
            )}

            {activeTab === 'social' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Redes Sociales
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Conecta tus redes sociales para compartir contenido automáticamente
                  </p>
                </div>

                <div className="space-y-4">
                  {[
                    { name: 'Instagram', connected: true, color: 'bg-pink-500' },
                    { name: 'Twitter', connected: false, color: 'bg-blue-500' },
                    { name: 'Facebook', connected: true, color: 'bg-blue-600' },
                    { name: 'TikTok', connected: false, color: 'bg-black' },
                    { name: 'YouTube', connected: false, color: 'bg-red-500' }
                  ].map((platform) => (
                    <div key={platform.name} className="flex items-center justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <div className="flex items-center">
                        <div className={`w-10 h-10 ${platform.color} rounded-lg flex items-center justify-center text-white font-bold mr-3`}>
                          {platform.name[0]}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900 dark:text-white">{platform.name}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {platform.connected ? 'Conectado' : 'No conectado'}
                          </div>
                        </div>
                      </div>
                      <button
                        className={`px-4 py-2 rounded-lg font-medium transition-colors duration-200 ${
                          platform.connected
                            ? 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/20 dark:text-red-400'
                            : 'bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/20 dark:text-green-400'
                        }`}
                      >
                        {platform.connected ? 'Desconectar' : 'Conectar'}
                      </button>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h4 className="text-md font-medium text-gray-900 dark:text-white mb-4">
                    Configuración de Compartir
                  </h4>
                  
                  <ToggleSwitch
                    checked={true}
                    onChange={() => {}}
                    label="Compartir logros automáticamente"
                    description="Publica tus nuevos logros en redes conectadas"
                  />

                  <ToggleSwitch
                    checked={false}
                    onChange={() => {}}
                    label="Compartir contenido multimedia"
                    description="Comparte fotos y videos automáticamente"
                  />

                  <ToggleSwitch
                    checked={true}
                    onChange={() => {}}
                    label="Compartir resultados de competencias"
                    description="Publica resultados de competencias importantes"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            Guardar Cambios
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;